import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Fade,
  Slide,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Autocomplete,
  Grid,
} from "@mui/material";
import StatusIcon from "@mui/icons-material/Info";
import { useSelector } from "react-redux";
import profileImage from "assets/MRwall.jpg";
import ScoreDashboard from "components/ScoreDashboard";
import {
  useGetMrStatusRecordQuery,
  useUploadPrescriptionMutation,
  useUploadDrDataMutation,
  useGetDocRepQuery,
  useUploadChemistPobMutation,
} from "state/api";

const StatusRecords = ({
  openStatusList,
  records,
  filterStatus,
  setFilterStatus,
}) => {
  const handleStatusChange = (event, newStatus) => {
    if (newStatus !== null) {
      setFilterStatus(newStatus);
    }
  };

  const filteredRecords = Array.isArray(records)
    ? records.filter((status) => status.STATUS === filterStatus)
    : [];

  return (
    <Slide direction="left" in={openStatusList} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 300,
          height: "100vh",
          zIndex: 1000,
          overflowY: "auto",
          backgroundColor: "transparent",
          backdropFilter: "blur(50px)",
        }}
        p={2}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          component="h2"
          color="text.primary"
          mb={2}
        >
          Status Records
        </Typography>
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={handleStatusChange}
          sx={{ marginBottom: 2 }}
        >
          <ToggleButton value="APPROVED" color="success">
            Approved
          </ToggleButton>
          <ToggleButton value="REJECTED" color="error">
            Rejected
          </ToggleButton>
          <ToggleButton value="PENDING" color="warning">
            Pending
          </ToggleButton>
        </ToggleButtonGroup>
        <Grid container spacing={1}>
          {filteredRecords.length > 0 ? (
            filteredRecords.map((status, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    padding: 1,
                    backgroundColor: "transparent",
                    backdropFilter: "blur(50px)",
                  }}
                >
                  <Typography variant="body1">
                    {`Dr Name: ${status.DoctName}`} <br />
                    {`Submit Date: ${new Date(status.SUBDATE).toLocaleDateString("en-GB")}`}{" "}
                    <br />
                    {`Status: ${status.STATUS}`}
                  </Typography>
                </Paper>
              </Grid>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              No records available for{" "}
              {filterStatus?.toLowerCase() || "selected"} status.
            </Typography>
          )}
        </Grid>
      </Box>
    </Slide>
  );
};

export default function Mr() {
  const [doctCatId, setDoctCatId] = useState(null);
  const [hqMstId, setHqMstId] = useState(null);
  const [isPrescription, setIsPrescription] = useState(true);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [openShutter, setOpenShutter] = useState(true);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [mclCode, setMclCode] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openStatusList, setOpenStatusList] = useState(false);
  const [filterStatus, setFilterStatus] = useState("APPROVED");
  const [selectedTab, setSelectedTab] = useState("PRESCRIPTION");
  const [invoiceFileError, setInvoiceFileError] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoice, setInvoice] = useState({
    strips: "",
    chemistName: "",
    chemistAddress: "",
    dlNo: "234567",
    state: "",
    email: "demo@gmail.com",
    phone: "",
  });
  const MgCd = useSelector((state) => state.global.MgCd);
  const EmpNo = useSelector((state) => state.global.EmpNo);
  const { data: docReps = [], error } = useGetDocRepQuery(MgCd);
  const [uploadPrescription] = useUploadPrescriptionMutation();
  const [uploadDoctorData] = useUploadDrDataMutation();
  const [uploadInvoice] = useUploadChemistPobMutation();
  const { data: mrStatusResponse } = useGetMrStatusRecordQuery(EmpNo);
  const mrStatusRecord = mrStatusResponse?.data || [];
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    if (error) {
      console.error("Error fetching document representations:", error);
      setSnackbarMessage("Failed to fetch document representations.");
      setOpenSnackbar(true);
    }
  }, [error]);

  useEffect(() => {
    if (docReps.length > 0) {
      setHqMstId(docReps[0].HqMstId);
    }
  }, [docReps]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) {
        setFileError("File size should be less than 3MB");
        setPrescriptionFile(null);
        setSnackbarMessage("File size exceeds 3MB limit");
        setOpenSnackbar(true);
      } else {
        setFileError(null);
        setPrescriptionFile(file);
      }
    }
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      if (/^\d{0,10}$/.test(value)) {
        setInvoice((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setInvoice((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleInvoiceClear = () => {
    setInvoice({
      strips: "",
      chemistName: "",
      chemistAddress: "",
      dlNo: "234567",
      state: "",
      email: "demo@gmail.com",
      phone: "",
    });
    setInvoiceFile(null);
    setInvoiceFileError("");
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) {
        setInvoiceFileError("File size should be less than 3MB");
        setInvoiceFile(null);
        setSnackbarMessage("File size exceeds 3MB limit");
        setOpenSnackbar(true);
      } else {
        setInvoiceFileError(null);
        setInvoiceFile(file);
      }
    }
  };

  const resetFields = (type) => {
    setSelectedDoctor("");
    setSelectedSpecialty("");
    setMclCode("");
    setPrescriptionFile(null);
    setSelectedProduct("");
    setFileError(null);
  };

  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setIsPrescription(newAlignment === "prescription");
      resetFields(newAlignment);
    }
  };

  const handlePrescriptionSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !prescriptionFile ||
      !selectedProduct ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields and upload the prescription.",
      );
      setOpenSnackbar(true);
      return;
    }

    const prescriptionFormData = new FormData();
    prescriptionFormData.append("doctorName", selectedDoctor.name);
    prescriptionFormData.append("specialty", selectedSpecialty);
    prescriptionFormData.append("mclCode", mclCode);
    prescriptionFormData.append("prescription", prescriptionFile);
    prescriptionFormData.append("MgCd", MgCd);
    prescriptionFormData.append("EmpNo", EmpNo);
    prescriptionFormData.append("product", selectedProduct);

    try {
      await uploadPrescription(prescriptionFormData).unwrap();
      setSnackbarMessage("Prescription uploaded successfully!");
      setOpenSnackbar(true);
      resetFields("prescription");
    } catch (error) {
      console.error("Error uploading prescription:", error);
      setSnackbarMessage("Failed to upload prescription.");
      setOpenSnackbar(true);
    }
  };

  const handleDoctorSubmit = async (event) => {
    event.preventDefault();
    if (!selectedDoctor || !selectedSpecialty) {
      setSnackbarMessage("Please fill in all required fields.");
      setOpenSnackbar(true);
      return;
    }

    const doctorData = {
      MgCd: MgCd,
      HqMstId: hqMstId,
      doctorName: selectedDoctor.name,
      DoctCatId: doctCatId,
      UpdatedByEmpNo: EmpNo,
    };

    try {
      await uploadDoctorData(doctorData).unwrap();
      setSnackbarMessage("Doctor created successfully!");
      setOpenSnackbar(true);
      resetFields("doctor");
    } catch (error) {
      console.error("Error creating doctor:", error);
      setSnackbarMessage("Failed to create doctor.");
      setOpenSnackbar(true);
    }
  };

  const handleInvoiceSubmit = async (event) => {
    event.preventDefault();
    if (
      !selectedDoctor ||
      !selectedSpecialty ||
      !mclCode ||
      !invoice.strips ||
      !invoice.chemistName ||
      !invoice.chemistAddress ||
      !invoice.state ||
      !invoice.phone ||
      !invoiceFile ||
      !MgCd ||
      !EmpNo
    ) {
      setSnackbarMessage(
        "Please fill in all required fields and upload the invoice.",
      );
      setOpenSnackbar(true);
      return;
    }

    const invoiceFormData = new FormData();
    invoiceFormData.append("doctorName", selectedDoctor.name);
    invoiceFormData.append("specialty", selectedSpecialty);
    invoiceFormData.append("mclCode", mclCode);
    invoiceFormData.append("strips", invoice.strips);
    invoiceFormData.append("chemistName", invoice.chemistName);
    invoiceFormData.append("chemistAddress", invoice.chemistAddress);
    invoiceFormData.append("dlNo", invoice.dlNo);
    invoiceFormData.append("state", invoice.state);
    invoiceFormData.append("email", invoice.email);
    invoiceFormData.append("mobile", invoice.phone);
    invoiceFormData.append("invoiceFile", invoiceFile);
    invoiceFormData.append("MgCd", MgCd);
    invoiceFormData.append("EmpNo", EmpNo);

    try {
      await uploadInvoice(invoiceFormData).unwrap();
      setSnackbarMessage("Invoice uploaded successfully!");
      setOpenSnackbar(true);
      handleInvoiceClear();
    } catch (error) {
      console.error("Error uploading invoice:", error);
      setSnackbarMessage("Failed to upload invoice.");
      setOpenSnackbar(true);
    }
  };

  const handleSnackbarClose = () => setOpenSnackbar(false);

  const toggleStatusList = () => setOpenStatusList((prev) => !prev);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        backgroundImage: `url(${profileImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        p: 3,
        overflow: "auto",
      }}
    >
      <IconButton
        onClick={toggleStatusList}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 1000,
          color: "#80bf1b",
        }}
      >
        <StatusIcon />
      </IconButton>

      <StatusRecords
        openStatusList={openStatusList}
        records={mrStatusRecord}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      <div className="flex gap-2 mb-4">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              sx={{
                borderRadius: "9999px",
                px: 6,
                py: 2,
                fontSize: "0.875rem",
                fontWeight: "bold",
                boxShadow: 3,
                transition: "all 0.3s",
                ...(selectedTab === "PRESCRIPTION" && {
                  bgcolor: "primary.main",
                  color: "white",
                  transform: "scale(1.05)",
                }),
                ...(selectedTab !== "PRESCRIPTION" && {
                  bgcolor: "grey.100",
                  color: "grey.700",
                  "&:hover": {
                    bgcolor: "grey.200",
                  },
                }),
              }}
              onClick={() => setSelectedTab("PRESCRIPTION")}
            >
              PRESCRIPTION
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: "9999px",
                px: 6,
                py: 2,
                fontSize: "0.875rem",
                fontWeight: "bold",
                boxShadow: 3,
                transition: "all 0.3s",
                ...(selectedTab === "INVOICE" && {
                  bgcolor: "primary.main",
                  color: "white",
                  transform: "scale(1.05)",
                }),
                ...(selectedTab !== "INVOICE" && {
                  bgcolor: "grey.100",
                  color: "grey.700",
                  "&:hover": {
                    bgcolor: "grey.200",
                  },
                }),
              }}
              onClick={() => setSelectedTab("INVOICE")}
            >
              POB
            </Button>
          </Box>
        </Box>
      </div>

      {selectedTab === "PRESCRIPTION" && (
        <div>
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              maxWidth: 400,
              backgroundColor: "transparent",
              padding: 3,
              backdropFilter: "blur(50px)",
            }}
          >
            <ScoreDashboard empNo={EmpNo} />

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5" component="h2" color="text.primary">
                {isPrescription ? "Prescription Upload" : "Create Doctor"}
              </Typography>
              <ToggleButtonGroup
                value={isPrescription ? "prescription" : "doctor"}
                exclusive
                onChange={handleToggleChange}
                aria-label="upload type"
              >
                <ToggleButton
                  value="prescription"
                  aria-label="prescription upload"
                >
                  Prescription
                </ToggleButton>
                <ToggleButton value="doctor" aria-label="doctor creation">
                  Doctor
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <form
              onSubmit={
                isPrescription ? handlePrescriptionSubmit : handleDoctorSubmit
              }
              encType="multipart/form-data"
            >
              {isPrescription ? (
                <>
                  <Autocomplete
                    options={[
                      ...new Set(docReps.map((doc) => doc.DocCategory)),
                    ]}
                    value={selectedSpecialty}
                    onChange={(event, newValue) => {
                      setSelectedSpecialty(newValue || "");
                      setSelectedDoctor(null);
                      setMclCode("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialty"
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}
                  />

                  <Autocomplete
                    options={docReps
                      .filter((doc) => doc.DocCategory === selectedSpecialty)
                      .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedDoctor}
                    onChange={(event, newValue) => {
                      setSelectedDoctor(newValue);
                      setMclCode(newValue ? newValue.code : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dr. Name"
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="MCL Code"
                    variant="outlined"
                    margin="normal"
                    required
                    value={mclCode}
                    InputProps={{ readOnly: true }}
                  />

                  <Autocomplete
                    options={["Folireg-cd", "Indocapp", "Fe Protein","Rutrixa-B"]}
                    value={selectedProduct}
                    onChange={(event, newValue) => setSelectedProduct(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}
                  />

                  <Button variant="contained" component="label" fullWidth>
                    Upload Prescription
                    <input
                      type="file"
                      hidden
                      required
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </Button>

                  {fileError && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      {fileError}
                    </Typography>
                  )}

                  <Fade in={!!prescriptionFile && !fileError} timeout={500}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ marginTop: 1 }}
                    >
                      {prescriptionFile
                        ? `${prescriptionFile.name} (${(prescriptionFile.size / (1024 * 1024)).toFixed(2)} MB)`
                        : ""}
                    </Typography>
                  </Fade>

                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ marginTop: 2 }}
                    type="submit"
                    disabled={!!fileError || !prescriptionFile}
                  >
                    Submit
                  </Button>
                </>
              ) : (
                <>
                  <Autocomplete
                    options={[
                      ...new Set(docReps.map((doc) => doc.DocCategory)),
                    ]}
                    value={selectedSpecialty}
                    onChange={(event, newValue) => {
                      setSelectedSpecialty(newValue || "");
                      const selectedDoc = docReps.find(
                        (doc) => doc.DocCategory === newValue,
                      );
                      setDoctCatId(selectedDoc ? selectedDoc.DoctCatId : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specialty"
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ marginTop: 2 }}
                      />
                    )}
                  />
                  <TextField
                    label="New Doctor Name"
                    variant="outlined"
                    fullWidth
                    value={selectedDoctor.name}
                    onChange={(e) =>
                      setSelectedDoctor({ name: e.target.value })
                    }
                    required
                    sx={{ marginTop: 2 }}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={{ marginTop: 2 }}
                    type="submit"
                  >
                    Submit
                  </Button>
                </>
              )}
            </form>

            <Snackbar
              open={openSnackbar}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              message={snackbarMessage}
            />
          </Paper>
        </div>
      )}

      {selectedTab === "INVOICE" && (
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 400,
            backgroundColor: "transparent",
            padding: 3,
            mb: 10,
            backdropFilter: "blur(50px)",
          }}
        >
          <Typography variant="h5" component="h2" color="text.primary" mb={2}>
            Invoice Upload
          </Typography>

          <form onSubmit={handleInvoiceSubmit} encType="multipart/form-data">
            <Autocomplete
              options={[...new Set(docReps.map((doc) => doc.DocCategory))]}
              value={selectedSpecialty}
              onChange={(event, newValue) => {
                setSelectedSpecialty(newValue || "");
                setSelectedDoctor(null);
                setMclCode("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Specialty"
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />
              )}
            />

            <Autocomplete
              options={docReps
                .filter((doc) => doc.DocCategory === selectedSpecialty)
                .map((doc) => ({ name: doc.DoctName, code: doc.DoctCd }))}
              getOptionLabel={(option) => option.name || ""}
              value={selectedDoctor}
              onChange={(event, newValue) => {
                setSelectedDoctor(newValue);
                setMclCode(newValue ? newValue.code : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dr. Name"
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                />
              )}
            />

            <TextField
              fullWidth
              label="MCL Code"
              variant="outlined"
              margin="normal"
              required
              value={mclCode}
              InputProps={{
                readOnly: true,
              }}
            />
            <input type="hidden" name="mclCode" value={mclCode} />

            <TextField
              fullWidth
              label="No. of Strips"
              variant="outlined"
              margin="normal"
              name="strips"
              value={invoice.strips}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="Chemist Name"
              variant="outlined"
              margin="normal"
              name="chemistName"
              value={invoice.chemistName}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="Chemist Address"
              variant="outlined"
              margin="normal"
              name="chemistAddress"
              value={invoice.chemistAddress}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              fullWidth
              label="State"
              variant="outlined"
              margin="normal"
              name="state"
              value={invoice.state}
              onChange={handleInvoiceChange}
              required
            />

            <TextField
              label="Phone Number"
              variant="outlined"
              fullWidth
              name="phone"
              value={invoice.phone}
              onChange={handleInvoiceChange}
              error={invoice.phone.length > 0 && invoice.phone.length !== 10}
              helperText={
                invoice.phone.length > 0 && invoice.phone.length !== 10
                  ? "Phone number must be exactly 10 digits"
                  : ""
              }
              required
            />

            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{ marginTop: 2 }}
            >
              Upload Invoice
              <input
                type="file"
                hidden
                required
                onChange={handleInvoiceFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                name="invoiceFile"
              />
            </Button>

            {invoiceFileError && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {invoiceFileError}
              </Typography>
            )}

            <Fade in={!!invoiceFile} timeout={500}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {invoiceFile
                  ? `${invoiceFile.name} (${(invoiceFile.size / (1024 * 1024)).toFixed(2)} MB)`
                  : ""}
              </Typography>
            </Fade>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ marginRight: 1 }}
                onClick={handleInvoiceClear}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                color="success"
                fullWidth
                type="submit"
                disabled={invoice.phone?.length !== 10}
              >
                Submit
              </Button>
            </Box>
          </form>
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
          />
        </Paper>
      )}
    </Box>
  );
}
